import { useSyncExternalStore } from 'react';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { toast, toastStore, type ToastTone } from './toast-store';

const icons = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
  warning: TriangleAlert,
} as const;
const tones: Record<ToastTone, string> = {
  warning: 'border-warning text-warning',
  success: 'border-success text-success',
  error: 'border-danger text-danger',
  info: 'border-info text-info',
};

/** Mounted once in the app shell ("feature toast area"). aria-live so screen readers announce messages. */
export function ToastViewport() {
  const list = useSyncExternalStore(toastStore.subscribe, toastStore.get, toastStore.get);
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-full max-w-sm flex-col gap-2"
    >
      {list.map((t) => {
        const Icon = icons[t.tone];
        return (
          <div
            key={t.id}
            role={t.tone === 'error' ? 'alert' : 'status'}
            className={cn(
              'pointer-events-auto flex items-start gap-3 rounded-lg border-l-4 bg-surface px-4 py-3 text-sm text-fg shadow-pop ring-1 ring-border',
              tones[t.tone],
            )}
          >
            <Icon aria-hidden className="mt-0.5 size-5 shrink-0" />
            <p className="min-w-0 flex-1 text-fg">{t.message}</p>
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={() => toast.dismiss(t.id)}
              className="rounded p-0.5 text-fg-subtle hover:text-fg"
            >
              <X className="size-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
